import { useLocation } from "react-router-dom";
import { IconButton, Typography, useTheme } from "@mui/material";
import { useParams } from "react-router-dom";
import SubPageLayout from "v2/layouts/SubPageLayout";
import useToastStore from "store/toast-store/useToastStore";
import { useNavigatePreserveQuery } from "lib/hooks/useNavigatePreserveQuery";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axiosService from "services/axios";
import { ROUTES } from "consts";
import { CHAIN_CHECK_LIST, CHAIN_CONFIG, Chain } from "chainConfigs";
import { Card, Circle, Col, Row } from "v2/components/base/Flex/ui";
import ScrollableLoading from "v2/components/base/Scrollable/ui/ScrollableLoading/v1";
import ScrollableEmpty from "v2/components/base/Scrollable/ui/ScrollableEmpty/v1";
import DetailPageLayout from "v2/layouts/DetailPageLayout";
import { TradeProvider, useTradeContext } from "contexts/TradeContext";
import CoinAvatar from "v2/components/CoinAvatar/ui";
import { Cached, Share } from "@mui/icons-material";
import { copyToClipboard } from "utils";
import BasicButton from "v2/components/Buttons/BasicButton";
import { Value } from "components/Value";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { TokenPrice } from "types";
import { formatDistanceStrict, startOfMinute, startOfWeek } from "date-fns";
import { startOfHour } from "date-fns";
import { startOfDay } from "date-fns";
import Skeleton from "v2/components/base/Skeleton/ui";
import { PercentageBlock } from "v2/components/NumBlock/ui";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import Divider from "v2/components/base/Divider/ui";
import { TokenDetailChart } from "components/Chart/TokenDetailChart";
import Tabs from "v2/components/base/Tabs/ui";
import useTabs from "v2/components/base/Tabs/hooks/useTabs";
import { CHAIN_BADGE_MAP } from "v2/components/CoinAvatar/ui/CoinAvatar/v1";
import { ReactComponent as LogoIcon } from "assets/icons/logo.svg";
import { CrossChainDepositModal } from "components/Deposit/DepositModal";
import useTradeStore from "store/trade-store.ts/useTradeStore";
import { useWsPriceData } from "pages/token-id/hook";
import CrossChainSwapDrawer from "v2/components/CrossChainSwapDrawer";
import { checkIsMobile } from "lib/utils";
import { useCheckViewPoint, VIEW_POINT_CONFIG } from "hooks/useCheckViewPoint";
import { StyledImg } from "pages/jetton/dataRow/trade/styled";
import { CopyToClipboard } from "react-copy-to-clipboard";
import CopyImg from "assets/icons/copy-brand.svg";
import { AuthCheckBlocker } from "components/Blockers";

const groupingFunction = {
  minute: startOfMinute,
  hour: startOfHour,
  day: startOfDay,
  week: startOfWeek,
};

export const TokenIdPage = () => {
  const { search } = useLocation();
  const isFromTokenCall = new URLSearchParams(search).get("from") === "cabal";
  const isFromPortfolio = new URLSearchParams(search).get("from") === "portfolio";
  const navigate = useNavigatePreserveQuery();

  const handleBack = () => {
    const searchParams = new URLSearchParams(search);

    if (isFromTokenCall) {
      const cabalId = searchParams.get("cabalId");
      searchParams.delete("from");
      searchParams.delete("cabalId");
      if (cabalId) {
        navigate(ROUTES.cabal + `/${cabalId}`);
      } else {
        navigate(ROUTES.explorer);
      }
    } else if (isFromPortfolio) {
      searchParams.delete("from");
      navigate(ROUTES.portfolio);
    } else {
      searchParams.delete("from");
      navigate(ROUTES.trade);
    }
  };

  return (
    <SubPageLayout handleBack={handleBack} backToTitle="Back">
      <AuthCheckBlocker>
        <TokenIdPageBody />
      </AuthCheckBlocker>
    </SubPageLayout>
  );
};

const TokenIdPageBody = () => {
  const theme = useTheme();
  const { id } = useParams();
  const { search, pathname } = useLocation();
  const chain = new URLSearchParams(search).get("chain");
  const isFromTokenCall = new URLSearchParams(search).get("from") === "cabal";
  const isFromPortfolio = new URLSearchParams(search).get("from") === "portfolio";
  const { showToast } = useToastStore();
  const navigate = useNavigatePreserveQuery();
  const { data: tokenData, isLoading } = useQuery({
    queryKey: ["token", id],
    queryFn: () => {
      return axiosService.getToken({ tokenAddress: id!, chain: chain as Chain });
    },
    enabled: !!id && !!chain,
  });

  if (!chain) {
    showToast({
      variant: "error",
      message: "Chain is required for token detail page.",
      duration: 3000,
    });
    navigate(ROUTES.trade);
    return <></>;
  }

  const nativeToken = CHAIN_CONFIG[chain as Chain];
  const targetToken =
    nativeToken?.wrappedTokenAddress === tokenData?.tokenAddress
      ? { ...nativeToken, tokenAddress: nativeToken.nativeTokenAddress }
      : tokenData;

  const handleBack = () => {
    const searchParams = new URLSearchParams(search);

    if (isFromTokenCall) {
      const cabalId = searchParams.get("cabalId");
      searchParams.delete("from");
      searchParams.delete("cabalId");
      if (cabalId) {
        navigate(ROUTES.cabal + `/${cabalId}`);
      } else {
        navigate(ROUTES.explorer);
      }
    } else if (isFromPortfolio) {
      searchParams.delete("from");
      navigate(ROUTES.portfolio);
    } else {
      searchParams.delete("from");
      navigate(ROUTES.trade);
    }
  };

  return isLoading ? (
    <Col type="cc" sx={{ height: "100vh" }} fullWidth>
      <ScrollableLoading />
    </Col>
  ) : !tokenData ? (
    <Col type="cc" sx={{ height: "100vh" }} fullWidth>
      <ScrollableEmpty />
    </Col>
  ) : id && chain ? (
    <TradeProvider
      chain={chain as Chain}
      targetTokenConfig={{
        [chain]: {
          chain,
          symbol: targetToken?.symbol,
          token: tokenData.tokenAddress,
          imageUrl: targetToken?.imageUrl,
        },
      }}>
      <DetailPageLayout
        isSubPage
        FixedContent={<TokenOverview id={id} tokenData={tokenData} />}
        ScrollableContent={<TokenDetails id={id} tokenData={tokenData} />}
      />
    </TradeProvider>
  ) : null;
};

const TokenOverview = ({ id, tokenData }: { id: string; tokenData: any }) => {
  const { search, state: navStates } = useLocation();
  const groupId = new URLSearchParams(search).get("groupId");
  const [shouldTradeDrawerOpen, setShouldTradeDrawerOpen] = useState(false);
  const [timeInterval, setTimeInterval] = useState<"minute" | "hour" | "day" | "week">("hour");
  const [priceDataConsolidated, setPriceDataConsolidated] = useState<TokenPrice[]>([]);
  const [isCrossChainDepositModalOpen, setIsCrossChainDepositModalOpen] = useState(false);
  const [isReadyToTrade, setIsReadyToTrade] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [tempBalanceData, setTempBalanceData] = useState<any | null>(null);
  const [latestPrice, setLatestPrice] = useState<TokenPrice | null>(null);
  const [isLoadedBalance, setIsLoadedBalance] = useState(false);
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  const [isHidePrice, setIsHidePrice] = useState(false);

  const queryClient = useQueryClient();

  const { walletAddresses, tgUserId, tgUserName, getTgUserIdV2, createWallet, hasWallet } =
    useUserStoreV2();

  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);

  const { isTrading, refetchTargetTokenBalance } = useTradeContext();

  const { getUserBalance, updateUserBalance } = useTradeStore();

  const { showToast } = useToastStore();

  const priceDataRef = useRef<TokenPrice[]>([]);

  const isTradeInitingRef = useRef(false);

  const {
    data: priceData,
    isLoading: isPriceDataLoading,
    isSuccess: isPriceDataSuccess,
    isRefetching: isPriceDataRefetching,
    refetch: refetchPriceData,
  } = useQuery({
    queryKey: ["token-price", { id, chain: tokenData?.chain!, timeInterval }],
    queryFn: () =>
      axiosService.getCabalCallsPrice({
        pairAddress: tokenData?.tokenAddress!,
        chain: tokenData?.chain!,
        interval: timeInterval,
      }),
    enabled: !!tokenData?.chain,
  });

  const { data: tokenNativePrice, refetch: refetchTokenNativePrice } = useQuery({
    queryKey: ["tokenNativePrice"],
    queryFn: () =>
      axiosService.getNativePrice({
        tokenAddress: tokenData?.tokenAddress!,
        chain: tokenData?.chain!,
      }),
    enabled: !!tokenData?.tokenAddress && !!tokenData?.chain,
  });

  const {
    data: balanceData,
    isLoading: isBalanceLoading,
    isRefetching: isBalanceRefetching,
    refetch: refetchBalanceData,
  } = useQuery({
    queryKey: ["token-balance", id, hasWallet],
    queryFn: () => {
      if (!id || !tokenData) return;
      if (!hasWallet || !walletAddresses) {
        setIsLoadedBalance(true);
        return;
      }

      const result = axiosService.getTokenBalance({
        tokenAddress: id,
        walletAddress: walletAddresses[CHAIN_CONFIG[tokenData!.chain as Chain].walletName]!,
        chain: tokenData.chain,
      });
      setIsLoadedBalance(true);
      return result;
    },
    enabled: !!id && !!tokenData,
  });

  // TEMP FIX to also invalidate data in the trade logic
  const refetchBalance = () => {
    queryClient.invalidateQueries({ queryKey: ["token-balance"] });
  };

  useEffect(() => {
    setIsHidePrice(!!id);
  }, [id]);

  useEffect(() => {
    if (balanceData) {
      setTempBalanceData({ ...balanceData });
    }
  }, [balanceData]);

  useEffect(() => {
    if (tokenData && isLoadedBalance) {
      setIsReadyToTrade(true);
    } else {
      setIsReadyToTrade(false);
    }
  }, [tokenData, isLoadedBalance]);

  useEffect(() => {
    if (priceData && priceData.length > 0) {
      priceDataRef.current = [];
      setPriceDataConsolidated([]);
      const groupedData: TokenPrice[] = priceData.reduce((acc: TokenPrice[], cur: TokenPrice) => {
        // 1st data price is null, ignore
        if (!acc.length && cur.price === null) {
          return acc;
        }

        // 1st data
        let roundedTime = groupingFunction[timeInterval](new Date(cur.updatedAt)).toISOString();
        if (!acc.length) {
          acc.push({
            updatedAt: roundedTime,
            price: cur.price,
          } as TokenPrice);
          return acc;
        }

        const latestData = acc[acc.length - 1];

        if (cur.price === null) {
          roundedTime = groupingFunction[timeInterval](latestData.updatedAt).toISOString();
        } else {
          roundedTime = groupingFunction[timeInterval](cur.updatedAt).toISOString();
        }

        const roundedTimeData = {
          updatedAt: roundedTime,
          price: cur.price,
        } as TokenPrice;

        if (latestData.updatedAt === roundedTime) {
          acc[acc.length - 1] = roundedTimeData;
          acc = [...acc];
        } else {
          acc = [...acc, roundedTimeData];
        }

        return acc;
      }, []);

      priceDataRef.current = groupedData;
      setPriceDataConsolidated(groupedData);
    }
  }, [priceData, timeInterval]);

  const transformPriceData = (data: TokenPrice) => {
    return {
      time: new Date(data.updatedAt).getTime(),
      value: data.price,
    };
  };

  const handleOnPriceDataV2 = useCallback(
    (priceData: TokenPrice) => {
      setLatestPrice(priceData);

      let roundedTime: string;
      const fullPriceData = priceDataRef.current;
      const latestData = fullPriceData[fullPriceData.length - 1];

      if (!latestData || !priceDataRef.current.length) {
        return null;
      }

      if (priceData.price === null) {
        roundedTime = groupingFunction[timeInterval](latestData.updatedAt).toISOString();
      } else {
        roundedTime = groupingFunction[timeInterval](priceData.updatedAt).toISOString();
      }

      const roundedTimeData = {
        updatedAt: roundedTime,
        price: priceData.price,
      };

      if (latestData.updatedAt === roundedTime) {
        setPriceDataConsolidated((prev) => {
          prev[prev.length - 1] = roundedTimeData;
          priceDataRef.current = [...prev];
          return [...prev];
        });
      } else {
        setPriceDataConsolidated((prev) => {
          priceDataRef.current = [...prev, roundedTimeData];
          return [...prev, roundedTimeData];
        });
      }
    },
    [timeInterval],
  );

  const handleLogin = async () => {
    try {
      if (!tgUserId || !tgUserName) {
        await getTgUserIdV2();
      }
    } catch (error) {
      console.error("Error while logging in", error);
    }
  };

  useWsPriceData({
    chain: tokenData?.chain!,
    masterAddress: tokenData?.tokenAddress!,
    onPriceData: handleOnPriceDataV2,
  });

  const theme = useTheme();

  const handleShare = async () => {
    const isMobile = checkIsMobile();
    if (!!navigator.share && isMobile) {
      try {
        await navigator.share({
          url: window.location.href,
        });
      } catch (e) {
        console.error("Error sharing the link", e);
      }
    } else {
      const url = new URL(window.location.href);
      url.searchParams.set("referralId", String(tgUserId || ""));
      copyToClipboard(url.toString());
      showToast({
        variant: "success",
        message: "Copied shared link to clipboard.",
        duration: 3000,
      });
    }
  };

  const onCopy = () => {
    showToast({
      variant: "success",
      message: "Copied address to clipboard",
      duration: 3000,
    });
  };

  return (
    <>
      <Col section="token-overview" type="ss" fullWidth gap="32px">
        <Row section="token-overview-header" type="bc" fullWidth>
          <Row section="token-info" type="sc" gap="18px" fullWidth>
            <Col section="token-info-image" type="ss">
              <CoinAvatar
                size={54}
                badgeSize={27}
                iconUri={tokenData.imageUrl}
                chain={tokenData.chain as Chain}
              />
            </Col>
            <Col section="token-info-name" type="ss" gap="10px">
              <Row sx={{ display: "inline-box" }}>
                <Typography variant="mobile-h3">{tokenData.tokenName}</Typography>
                <CopyToClipboard text={tokenData.tokenAddress ?? ""} onCopy={onCopy}>
                  <IconButton sx={{ padding: 0, margin: "0px 0px 8px 6px" }}>
                    <StyledImg src={CopyImg} />
                  </IconButton>
                </CopyToClipboard>
              </Row>
              <Row type="sc" fullWidth gap="8px">
                {latestPrice && latestPrice.price !== null ? (
                  <Value variant="mobile-h3" value={latestPrice.price} isPrice />
                ) : (
                  <Skeleton.Dot
                    isLoading={!tokenNativePrice || !tokenNativePrice.price}
                    skeletonHeight={24}
                    skeletonWidth={100}>
                    <Value
                      variant="mobile-h3"
                      value={tokenNativePrice?.price}
                      color="brand.primary"
                      isPrice
                    />
                  </Skeleton.Dot>
                )}
                <Cached
                  onClick={() => refetchPriceData()}
                  sx={{
                    cursor: "pointer",
                    ...((isPriceDataLoading || isPriceDataRefetching) && {
                      animation: "spin 2s linear infinite",
                      "@keyframes spin": {
                        "0%": {
                          transform: "rotate(360deg)",
                        },
                        "100%": {
                          transform: "rotate(0deg)",
                        },
                      },
                    }),
                  }}
                />
              </Row>
              {/* ) : ( */}
              {/* <Typography variant="mobile-h3" color="text.disabled"> */}
              {/* Loading... */}
              {/* </Typography> */}
              {/* )} */}
            </Col>
          </Row>
          <BasicButton variant="black" onClick={handleShare}>
            <Share />
          </BasicButton>
        </Row>
        <Row section="token-holding-info" type="sc" gap="10px" fullWidth>
          <Col fullWidth gap="8px">
            <Row type="bc" fullWidth>
              <Typography variant="mobile-body-m-heavy">Your {tokenData.symbol} Holding</Typography>
              <Cached
                onClick={() => refetchBalance()}
                sx={{
                  cursor: "pointer",
                  ...((isBalanceLoading || isBalanceRefetching) && {
                    animation: "spin 2s linear infinite",
                    "@keyframes spin": {
                      "0%": {
                        transform: "rotate(360deg)",
                      },
                      "100%": {
                        transform: "rotate(0deg)",
                      },
                    },
                  }),
                }}
              />
            </Row>
            <Card.Section sx={{ width: "100%" }}>
              <Col fullWidth gap="24px">
                <Typography variant="mobile-body-s">USD Value</Typography>
                <Row type="bc" fullWidth>
                  <Skeleton.Dot
                    isLoading={isBalanceLoading || isBalanceRefetching}
                    skeletonHeight={50}
                    skeletonWidth={100}>
                    <Value
                      variant="mobile-display-m"
                      color="brand.primary"
                      value={balanceData?.usdValue ?? 0}
                      isPrice
                    />
                    <PercentageBlock percentage={balanceData?.performance ?? 0} />
                  </Skeleton.Dot>
                </Row>
                <Col fullWidth>
                  <Row type="bc" fullWidth>
                    <Typography variant="mobile-body-m" color="text.secondary">
                      Quantity
                    </Typography>
                    <Skeleton.Dot
                      isLoading={isBalanceLoading}
                      skeletonHeight={24}
                      skeletonWidth="fit-content">
                      <Value variant="mobile-display-s" value={balanceData?.quantity ?? 0} />
                    </Skeleton.Dot>
                  </Row>
                  <Divider.Row />
                  <Row type="bc" fullWidth>
                    <Typography variant="mobile-body-m" color="text.secondary">
                      P&L
                    </Typography>
                    <Skeleton.Dot
                      isLoading={isBalanceLoading}
                      skeletonHeight={24}
                      skeletonWidth="fit-content">
                      <Typography
                        variant="mobile-display-s"
                        color={
                          balanceData?.profit > 0
                            ? "system.green-alarm"
                            : !balanceData?.profit
                            ? "text.secondary"
                            : "system.red-alarm"
                        }>
                        {balanceData?.profit > 0
                          ? "+"
                          : !balanceData?.profit
                          ? ""
                          : balanceData?.profit.toString()?.split("")[0] === "-"
                          ? ""
                          : "-"}
                        <Value variant="mobile-display-s" value={balanceData?.profit ?? 0} /> USD
                      </Typography>
                    </Skeleton.Dot>
                  </Row>
                </Col>
              </Col>
            </Card.Section>
          </Col>
        </Row>
        <TradeButton
          isMobile={isMobile}
          isTrading={isTrading}
          isReadyToTrade={isReadyToTrade}
          isProcessing={isProcessing}
          isCreatingWallet={isCreatingWallet}
          hasWallet={hasWallet}
          setIsCrossChainDepositModalOpen={setIsCrossChainDepositModalOpen}
          setIsProcessing={setIsProcessing}
          setShouldTradeDrawerOpen={setShouldTradeDrawerOpen}
          isTradeInitingRef={isTradeInitingRef}
          setIsCreatingWallet={setIsCreatingWallet}
          updateUserBalance={updateUserBalance}
        />
      </Col>
      <CrossChainSwapDrawer
        open={shouldTradeDrawerOpen}
        groupId={parseInt(groupId ?? "0")}
        chain={tokenData.chain as Chain}
        isFromTrendingCall={navStates?.isFromTrendingCall}
        onConfirm={() => {
          setShouldTradeDrawerOpen(false);
        }}
        onFail={() => {
          setIsProcessing(false);
          setShouldTradeDrawerOpen(false);
          updateUserBalance();
          refetchBalanceData();
          refetchBalance();
        }}
        onProcessing={() => {
          setIsProcessing(true);
        }}
        onProcessed={() => {
          refetchBalanceData();
          refetchBalance();
          setIsProcessing(false);
          updateUserBalance();
          refetchTargetTokenBalance();
        }}
        onClose={() => {
          setIsReadyToTrade(true);
          setShouldTradeDrawerOpen(false);
          refetchBalanceData();
          refetchBalance();
          updateUserBalance();
          refetchTargetTokenBalance();
        }}
      />
      <CrossChainDepositModal
        defaultChain={tokenData.chain as Chain}
        chains={CHAIN_CHECK_LIST}
        isOpen={isCrossChainDepositModalOpen}
        close={() => setIsCrossChainDepositModalOpen(false)}
        onCopySuccess={() => {
          showToast({
            variant: "success",
            message: "Copied address to clipboard",
            duration: 3000,
          });
        }}
      />
    </>
  );
};

const TradeButton = ({
  isMobile,
  isTrading,
  isReadyToTrade,
  isProcessing,
  isCreatingWallet,
  hasWallet,
  updateUserBalance,
  setIsProcessing,
  setShouldTradeDrawerOpen,
  isTradeInitingRef,
  setIsCreatingWallet,
  setIsCrossChainDepositModalOpen,
}: {
  isMobile: boolean;
  isTrading: boolean;
  isReadyToTrade: boolean;
  isProcessing: boolean;
  isCreatingWallet: boolean;
  hasWallet: boolean;
  updateUserBalance: () => Promise<any>;
  setIsProcessing: (isProcessing: boolean) => void;
  setShouldTradeDrawerOpen: (shouldTradeDrawerOpen: boolean) => void;
  isTradeInitingRef: MutableRefObject<boolean>;
  setIsCreatingWallet: (isCreatingWallet: boolean) => void;
  setIsCrossChainDepositModalOpen: (isCrossChainDepositModalOpen: boolean) => void;
}) => {
  return (
    <BasicButton
      sx={{
        ...(isMobile && {
          position: "fixed",
          bottom: "24px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
        }),
      }}
      variant={
        isTrading || !isReadyToTrade || isProcessing || isCreatingWallet ? "disabled" : "primary"
      }
      fullWidth
      disabled={isTrading || !isReadyToTrade || isProcessing || isCreatingWallet}
      onClick={async () => {
        try {
          if (isProcessing || isTradeInitingRef.current) return;

          setIsProcessing(true);
          isTradeInitingRef.current = true;

          if (!hasWallet) {
            setIsCreatingWallet(true);
            setIsCreatingWallet(false);
          }

          setShouldTradeDrawerOpen(true);
          const { amount } = (await updateUserBalance()) ?? {};

          const isAllBalanceEmpty =
            amount &&
            CHAIN_CHECK_LIST.every(
              (key: string) =>
                !amount[key as keyof typeof amount] || amount[key as keyof typeof amount] <= 0,
            );

          if (!hasWallet || isAllBalanceEmpty) {
            updateUserBalance();
            setShouldTradeDrawerOpen(false);
            setIsCrossChainDepositModalOpen(true);
          }
        } catch (error) {
          console.error("Error while initing trade drawer", error);
        } finally {
          if (isCreatingWallet) {
            setIsCreatingWallet(false);
          }
          setIsProcessing(false);
          isTradeInitingRef.current = false;
        }
      }}>
      <Typography
        variant="mobile-action-s-heavy"
        color={
          isTrading || !isReadyToTrade || isProcessing || isCreatingWallet
            ? "text.disabled"
            : "text.btn-dark"
        }>
        <Skeleton.Dot
          isLoading={isTrading || !isReadyToTrade || isProcessing || isCreatingWallet}
          skeletonHeight={24}
          skeletonWidth="fit-content">
          Trade
        </Skeleton.Dot>
      </Typography>
    </BasicButton>
  );
};

const TokenDetails = ({ id, tokenData }: { id: string; tokenData: any }) => {
  const theme = useTheme();
  const [priceDataConsolidated, setPriceDataConsolidated] = useState<TokenPrice[]>([]);
  const [timeInterval, setTimeInterval] = useState<"minute" | "hour" | "day" | "week">("hour");
  const priceDataRef = useRef<TokenPrice[]>([]);
  const { selectedTab, handleTabChange } = useTabs(1);
  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);

  const {
    data: priceData,
    isLoading: isPriceDataLoading,
    isSuccess: isPriceDataSuccess,
  } = useQuery({
    queryKey: ["token-price", { id, chain: tokenData?.chain!, timeInterval }],
    queryFn: () =>
      axiosService.getCabalCallsPrice({
        pairAddress: tokenData?.tokenAddress!,
        chain: tokenData?.chain!,
        interval: timeInterval,
      }),
    enabled: !!tokenData?.chain,
  });

  useEffect(() => {
    if (priceData && priceData.length > 0) {
      priceDataRef.current = [];
      setPriceDataConsolidated([]);
      const groupedData: TokenPrice[] = priceData.reduce((acc: TokenPrice[], cur: TokenPrice) => {
        // 1st data price is null, ignore
        if (!acc.length && cur.price === null) {
          return acc;
        }

        // 1st data
        let roundedTime = groupingFunction[timeInterval](new Date(cur.updatedAt)).toISOString();
        if (!acc.length) {
          acc.push({
            updatedAt: roundedTime,
            price: cur.price,
          } as TokenPrice);
          return acc;
        }

        const latestData = acc[acc.length - 1];

        if (cur.price === null) {
          roundedTime = groupingFunction[timeInterval](latestData.updatedAt).toISOString();
        } else {
          roundedTime = groupingFunction[timeInterval](cur.updatedAt).toISOString();
        }

        const roundedTimeData = {
          updatedAt: roundedTime,
          price: cur.price,
        } as TokenPrice;

        if (latestData.updatedAt === roundedTime) {
          acc[acc.length - 1] = roundedTimeData;
          acc = [...acc];
        } else {
          acc = [...acc, roundedTimeData];
        }

        return acc;
      }, []);

      priceDataRef.current = groupedData;
      setPriceDataConsolidated(groupedData);
    }
  }, [priceData, timeInterval]);

  const transformPriceData = (data: TokenPrice) => {
    return {
      time: new Date(data.updatedAt).getTime(),
      value: data.price,
    };
  };

  const onTabChange = (tabIndex: number) => {
    setTimeInterval(["minute", "hour", "day"][tabIndex] as "minute" | "hour" | "day");
    handleTabChange(tabIndex);
  };

  return (
    <Col
      section="token-details"
      type="cc"
      gap="20px"
      sx={{ maxWidth: "555px", marginX: "auto" }}
      fullWidth>
      <Col section="token-details-chart" type="ss" gap="8px" fullWidth>
        <Typography variant="mobile-body-m-heavy">Chart</Typography>
        <Row type="cc" sx={{ aspectRatio: isMobile ? "6/5" : "6/4" }} fullWidth>
          <Skeleton.Logo isLoading={isPriceDataLoading}>
            {isPriceDataSuccess && !isPriceDataLoading && priceDataConsolidated.length > 0 ? (
              <Row type="cc" fullWidth>
                <TokenDetailChart data={priceDataConsolidated.map(transformPriceData)} />
              </Row>
            ) : (
              <Col type="cc" fullWidth>
                <Col type="cc" sx={{ padding: theme.spacing(2) }} fullWidth fullHeight>
                  <LogoIcon />
                  <Col
                    type="sc"
                    sx={{
                      color: "#fff",
                      gap: "10px",
                      marginTop: "24px",
                    }}>
                    <Typography variant="mobile-h2">No data yet</Typography>
                  </Col>
                </Col>
              </Col>
            )}
          </Skeleton.Logo>
        </Row>
        <Row fullWidth>
          <Tabs.Group
            sx={{ width: "100%", maxWidth: "330px", marginX: "auto" }}
            tabs={["1m", "1h", "1d"]}
            selectedTab={selectedTab}
            handleTabChange={onTabChange}
          />
        </Row>
      </Col>
      <Col section="token-details-more-info" type="cs" gap="8px" fullWidth>
        <Typography variant="mobile-body-m-heavy">More Info</Typography>
        <Card.Section sx={{ width: "100%" }}>
          <Col fullWidth>
            <Row type="bc" fullWidth>
              <Typography variant="mobile-body-m" color={theme.palette.text.secondary}>
                Network
              </Typography>
              <Row type="ec" gap="8px">
                <Circle.Image
                  sx={{
                    border: "1px solid #FFFFFF",
                  }}
                  size={24}
                  src={CHAIN_BADGE_MAP[tokenData.chain as Chain]}
                  alt={tokenData.chain}
                />
                <Typography variant="mobile-body-m-heavy" sx={{ textTransform: "capitalize" }}>
                  {tokenData.chain}
                </Typography>
              </Row>
            </Row>
            <Divider.Row />
            <Row type="bc" fullWidth>
              <Typography variant="mobile-body-m" color={theme.palette.text.secondary}>
                Market Cap
              </Typography>
              <Skeleton.Dot
                isLoading={!tokenData.marketCap}
                skeletonHeight={24}
                skeletonWidth="fit-content">
                <Typography variant="mobile-body-m-heavy">
                  <Value variant="mobile-body-m-heavy" value={tokenData.marketCap} isPrice /> USD
                </Typography>
              </Skeleton.Dot>
            </Row>
            <Divider.Row />
            <Row type="bc" fullWidth>
              <Typography variant="mobile-body-m" color={theme.palette.text.secondary}>
                Holders
              </Typography>
              <Skeleton.Dot
                isLoading={!tokenData.holders}
                skeletonHeight={24}
                skeletonWidth="fit-content">
                <Typography variant="mobile-body-m-heavy">
                  <Value variant="mobile-body-m-heavy" value={tokenData.holders} />
                </Typography>
              </Skeleton.Dot>
            </Row>
            <Divider.Row />
            <Row type="bc" fullWidth>
              <Typography variant="mobile-body-m" color={theme.palette.text.secondary}>
                Created
              </Typography>
              <Typography variant="mobile-body-m-heavy">
                {formatDistanceStrict(tokenData.pairCreatedAt, new Date(), {
                  addSuffix: true,
                })}
              </Typography>
            </Row>
          </Col>
        </Card.Section>
      </Col>
    </Col>
  );
};
